import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import '../../styles/auth/AuthPages.css';

/**
 * RegisterPage - Email/password registration
 */
export const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { register, isLoggingIn, error, clearError } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.email.trim()) {
      errors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Email invalide';
    }

    if (!formData.firstName.trim()) {
      errors.firstName = 'Le prénom est requis';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Le nom est requis';
    }

    if (!formData.password) {
      errors.password = 'Le mot de passe est requis';
    } else if (formData.password.length < 8) {
      errors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Veuillez confirmer le mot de passe';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear field error on change
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setSuccess(false);

    if (!validateForm()) return;

    const result = await register({
      email: formData.email,
      password: formData.password,
      first_name: formData.firstName,
      last_name: formData.lastName
    });

    if (result.success) {
      setSuccess(true);
    }
  };

  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-box">
          <header className="auth-header">
            <h1 className="auth-logo">Corpus</h1>
            <h2 className="auth-title">Inscription réussie !</h2>
          </header>

          <div className="auth-alert success" role="alert">
            <svg className="auth-alert-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
            <div className="auth-alert-content">
              <p className="auth-alert-title">Vérifiez votre email</p>
              <p className="auth-alert-message">
                Un email de vérification a été envoyé à <strong>{formData.email}</strong>.
                Cliquez sur le lien dans l'email pour activer votre compte.
              </p>
            </div>
          </div>

          <div className="auth-links">
            <Link to="/login" className="auth-link">
              Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-box">
        <header className="auth-header">
          <h1 className="auth-logo">Corpus</h1>
          <p className="auth-subtitle">Créez votre compte</p>
        </header>

        {error && (
          <div className="auth-alert error" role="alert">
            <svg className="auth-alert-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <div className="auth-alert-content">
              <p className="auth-alert-title">Erreur d'inscription</p>
              <p className="auth-alert-message">{error}</p>
            </div>
          </div>
        )}

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <div className="auth-field">
            <label className="auth-label" htmlFor="email">Email *</label>
            <input
              id="email"
              name="email"
              type="email"
              className={`auth-input ${fieldErrors.email ? 'error' : ''}`}
              value={formData.email}
              onChange={handleChange}
              placeholder="vous@exemple.com"
              autoComplete="email"
              disabled={isLoggingIn}
            />
            {fieldErrors.email && (
              <p className="auth-field-error">{fieldErrors.email}</p>
            )}
          </div>

          <div className="auth-field">
            <label className="auth-label" htmlFor="firstName">Prénom *</label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              className={`auth-input ${fieldErrors.firstName ? 'error' : ''}`}
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Votre prénom"
              autoComplete="given-name"
              disabled={isLoggingIn}
            />
            {fieldErrors.firstName && (
              <p className="auth-field-error">{fieldErrors.firstName}</p>
            )}
          </div>

          <div className="auth-field">
            <label className="auth-label" htmlFor="lastName">Nom *</label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              className={`auth-input ${fieldErrors.lastName ? 'error' : ''}`}
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Votre nom"
              autoComplete="family-name"
              disabled={isLoggingIn}
            />
            {fieldErrors.lastName && (
              <p className="auth-field-error">{fieldErrors.lastName}</p>
            )}
          </div>

          <div className="auth-field">
            <label className="auth-label" htmlFor="password">Mot de passe *</label>
            <div className="auth-password-field">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                className={`auth-input ${fieldErrors.password ? 'error' : ''}`}
                value={formData.password}
                onChange={handleChange}
                placeholder="Au moins 8 caracteres"
                autoComplete="new-password"
                disabled={isLoggingIn}
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                {showPassword ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            {fieldErrors.password && (
              <p className="auth-field-error">{fieldErrors.password}</p>
            )}
          </div>

          <div className="auth-field">
            <label className="auth-label" htmlFor="confirmPassword">Confirmer le mot de passe *</label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              className={`auth-input ${fieldErrors.confirmPassword ? 'error' : ''}`}
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Répétez le mot de passe"
              autoComplete="new-password"
              disabled={isLoggingIn}
            />
            {fieldErrors.confirmPassword && (
              <p className="auth-field-error">{fieldErrors.confirmPassword}</p>
            )}
          </div>

          <button type="submit" className="auth-submit" disabled={isLoggingIn}>
            {isLoggingIn ? (
              <>
                <span className="auth-spinner" />
                Inscription...
              </>
            ) : (
              'S\'inscrire'
            )}
          </button>
        </form>

        <div className="auth-links">
          <p className="auth-text">
            Deja un compte ? <Link to="/login">Se connecter</Link>
          </p>
        </div>
      </div>
    </div>
  );
};
